<template>
  <div class="gerai-manager">
    <h1>Manajemen Gerai</h1>
    <p class="subtitle">Kelola daftar gerai atau lokasi tes di sini.</p>

    <!-- Tombol untuk membuka modal -->
    <button @click="openModal()" class="action-btn primary">
      <i class="fas fa-plus"></i> Tambah Gerai Baru
    </button>

    <!-- Tabel Daftar Gerai -->
    <div class="card">
      <div v-if="isLoading">Memuat data gerai...</div>
      <div v-else-if="geraiList.length === 0">Belum ada gerai yang ditambahkan.</div>
      <div v-else class="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nama Gerai</th>
              <th>Alamat</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="gerai in geraiList" :key="gerai.id">
              <td>{{ gerai.id }}</td>
              <td>{{ gerai.name }}</td>
              <td>{{ gerai.address }}</td>
              <td class="actions">
                <button @click="openModal(gerai)" class="action-btn view-btn">Edit</button>
                <button @click="deleteGerai(gerai.id)" class="action-btn delete-btn">Hapus</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
    </div>

    <!-- Modal untuk Tambah/Edit Gerai -->
    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <h2>{{ isEditing ? 'Edit Gerai' : 'Tambah Gerai Baru' }}</h2>
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="gerai-name">Nama Gerai *</label>
            <input id="gerai-name" v-model="currentGerai.name" type="text" required />
          </div>
          <div class="form-group">
            <label for="gerai-address">Alamat (Opsional)</label>
            <textarea id="gerai-address" v-model="currentGerai.address" rows="3"></textarea>
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeModal" class="button-secondary">Batal</button>
            <button type="submit" :disabled="isSaving" class="button-primary">
              {{ isSaving ? 'Menyimpan...' : 'Simpan' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import apiClient from '@/services/api.js';

const geraiList = ref([]);
const isLoading = ref(true);
const error = ref('');

const isModalOpen = ref(false);
const isEditing = ref(false);
const isSaving = ref(false);
const currentGerai = ref({ id: null, name: '', address: '' });

const fetchGerai = async () => {
  isLoading.value = true;
  try {
    const response = await apiClient.get('/admin/gerai/');
    geraiList.value = response.data;
  } catch (err) {
    error.value = "Gagal memuat data gerai.";
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

const openModal = (gerai = null) => {
  if (gerai) {
    // Mode Edit
    isEditing.value = true;
    currentGerai.value = { ...gerai }; // Salin data agar tidak mengubah list secara langsung
  } else {
    // Mode Tambah Baru
    isEditing.value = false;
    currentGerai.value = { id: null, name: '', address: '' };
  }
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};

const handleSubmit = async () => {
  isSaving.value = true;
  try {
    const payload = { name: currentGerai.value.name, address: currentGerai.value.address };
    if (isEditing.value) {
      // Panggil API Update
      await apiClient.put(`/admin/gerai/${currentGerai.value.id}`, payload);
    } else {
      // Panggil API Create
      await apiClient.post('/admin/gerai/', payload);
    }
    closeModal();
    await fetchGerai(); // Muat ulang daftar setelah berhasil
  } catch (err) {
    alert("Gagal menyimpan: " + (err.response?.data?.detail || err.message));
  } finally {
    isSaving.value = false;
  }
};

const deleteGerai = async (geraiId) => {
  if (window.confirm("Yakin ingin menghapus gerai ini? Admin dan Paket Tes yang terhubung akan kehilangan asosiasi gerainya.")) {
    try {
      await apiClient.delete(`/admin/gerai/${geraiId}`);
      await fetchGerai(); // Muat ulang daftar
    } catch (err) {
      alert("Gagal menghapus gerai.");
      console.error(err);
    }
  }
};

onMounted(fetchGerai);
</script>

<style scoped>
.gerai-manager {
  max-width: 1024px;
  margin: auto;
  padding: 30px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

h1 {
  font-size: 1.8em;
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 5px;
}

.subtitle {
  color: #555;
  margin-top: 0;
  margin-bottom: 25px;
}

.card {
  margin-top: 25px;
  padding: 25px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 3px 10px rgba(0,0,0,0.06);
  border: 1px solid #eee;
}

/* Tombol Tambah Gerai */
.action-btn.primary {
  background-color: #1abc9c;
  color: white;
  border-color: #1abc9c;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 0.95em;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.action-btn.primary:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* Tabel */
.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  text-align: left;
  padding: 15px;
  border-bottom: 1px solid #eee;
}

th {
  background-color: #f8f9fa;
  font-weight: 500;
  color: #555;
  font-size: 0.95em;
}

.actions {
  display: flex;
  gap: 8px;
}

/* Tombol Aksi di Tabel */
.action-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background-color: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9em;
  transition: all 0.2s ease;
}
.action-btn:hover {
  opacity: 0.8;
}
.view-btn { /* Untuk tombol Edit */
  background-color: #3498db;
  color: white;
  border-color: #3498db;
}
.delete-btn {
  background-color: #e74c3c;
  color: white;
  border-color: #e74c3c;
}

/* State Loading dan Error */
.loading-placeholder, .error {
  padding: 20px;
  text-align: center;
  color: #777;
}
.error {
  color: #e74c3c;
}

/* CSS Khusus untuk Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  transition: opacity 0.3s ease;
}
.modal-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  transform: translateY(-20px);
  transition: all 0.3s ease;
}
.modal-content h2 {
  margin-top: 0;
  margin-bottom: 25px;
  color: #2c3e50;
}
.form-group {
  margin-bottom: 20px;
}
.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}
.form-group input, .form-group textarea {
  width: 100%;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  transition: border-color 0.2s;
}
.form-group input:focus, .form-group textarea:focus {
  outline: none;
  border-color: #1abc9c;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

/* Tombol di dalam Modal */
.button-primary, .button-secondary {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1em;
  font-weight: 500;
  transition: background-color 0.2s;
}
.button-primary {
  background-color: #1abc9c;
  color: white;
}
.button-primary:disabled {
  background-color: #bdc3c7;
}
.button-secondary {
  background-color: #ecf0f1;
  color: #2c3e50;
  border: 1px solid #bdc3c7;
}
</style>